import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  FileUploadField,
  FormModal,
  Input,
  Page,
  SegmentedControl,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { formatDate, pluralizeCzech } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import { useAdminSeasons } from "../../../lib/queries/seasons";
import {
  documentFileUrl,
  useCreateSeasonDocument,
  useRenameSeasonDocument,
  useReorderSeasonDocuments,
  useReplaceSeasonDocumentFile,
  useRetireSeasonDocument,
  useSeasonDocuments,
  type DocumentSource,
  type SeasonDocument,
} from "../../../lib/queries/seasonDocuments";
import type { ID } from "../../../lib/id";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

/** Mirrors seasondoc.MaxUploadBytes; the server is the one that refuses. */
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const PDF_ACCEPT = "application/pdf,.pdf";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "replace"; doc: SeasonDocument }
  | { kind: "rename"; doc: SeasonDocument };

function openPdf(versionId: ID): void {
  window.open(documentFileUrl(versionId), "_blank", "noopener");
}

/**
 * The documents a leader agrees to at „Registrace do sezóny" for one
 * season. Not sortable: the row order IS the order of the checkboxes, and
 * the move buttons write it.
 */
export function SeasonDocumentsPage() {
  const { data: me } = useMe();
  // Čtení všeho opens every document and who agreed, and changes none.
  const readOnly = readsOnlyAsGlobalViewer(me, "members_admin");
  const { id: seasonId = "" } = useParams<{ id: ID }>();
  const navigate = useNavigate();
  const { data: seasons } = useAdminSeasons();
  const season = seasons?.find((s) => s.id === seasonId);
  const { data: docs = [], isLoading, error, refetch } = useSeasonDocuments(seasonId);
  const live = useMemo(() => docs.filter((d) => !d.retired_at), [docs]);
  const retired = useMemo(() => docs.filter((d) => !!d.retired_at), [docs]);

  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const close = useCallback(() => setModal({ kind: "closed" }), []);
  const confirm = useConfirm();
  const reorder = useReorderSeasonDocuments(seasonId);
  const retire = useRetireSeasonDocument();

  const move = useCallback(
    (index: number, delta: number) => {
      const ids = live.map((d) => d.id);
      const target = index + delta;
      if (target < 0 || target >= ids.length) return;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      reorder.mutate(ids);
    },
    [live, reorder],
  );

  const askRetire = useCallback(
    (doc: SeasonDocument) =>
      confirm({
        title: "Vyřadit dokument",
        description: (
          <>
            Dokument <strong>{doc.name}</strong> se přestane nabízet ke souhlasu. Souhlasy, které
            týmy už daly, zůstanou zaznamenané.
          </>
        ),
        confirmLabel: "Vyřadit",
        variant: "danger",
        onConfirm: async () => {
          await retire.mutateAsync(doc.id);
        },
      }),
    [confirm, retire],
  );

  const columns = useMemo<Column<SeasonDocument>[]>(
    () => [
      {
        key: "name",
        header: "Název",
        render: (d) => (
          <>
            <div>{d.name}</div>
            {d.current && <Text variant="muted">{d.current.url || d.current.original_name}</Text>}
          </>
        ),
      },
      {
        key: "version",
        header: "Verze",
        render: (d) =>
          d.current ? (
            <>
              <div>{d.current.version}</div>
              <Text variant="muted">nahráno {formatDate(d.current.created_at)}</Text>
            </>
          ) : (
            "—"
          ),
      },
      {
        key: "agreed",
        header: "Souhlas",
        render: (d) =>
          d.retired_at ? (
            <Badge variant="muted">Vyřazený</Badge>
          ) : (
            <Link to={`/admin/sezony/${seasonId}/dokumenty/${d.id}`}>
              {d.agreed_teams} {pluralizeCzech(d.agreed_teams, ["tým", "týmy", "týmů"])}
            </Link>
          ),
      },
      {
        key: "actions",
        header: "",
        render: (d) => {
          const index = live.findIndex((x) => x.id === d.id);
          return (
            <Table.Actions>
              {d.current && (
                <Table.Action appearance="button" onClick={() => openPdf(d.current!.id)}>
                  Otevřít
                </Table.Action>
              )}
              <Table.Action appearance="button" onClick={() => navigate(`/admin/sezony/${seasonId}/dokumenty/${d.id}`)}>
                Souhlasy
              </Table.Action>
              {!readOnly && !d.retired_at && (
                <>
                  <Table.Action appearance="button" onClick={() => setModal({ kind: "replace", doc: d })}>
                    Nová verze
                  </Table.Action>
                  <Table.Action appearance="button" onClick={() => setModal({ kind: "rename", doc: d })}>
                    Přejmenovat
                  </Table.Action>
                  <Table.Action
                    appearance="button"
                    disabled={index <= 0 || reorder.isPending}
                    onClick={() => move(index, -1)}
                  >
                    Nahoru
                  </Table.Action>
                  <Table.Action
                    appearance="button"
                    disabled={index < 0 || index >= live.length - 1 || reorder.isPending}
                    onClick={() => move(index, 1)}
                  >
                    Dolů
                  </Table.Action>
                  <Table.Action appearance="button" variant="danger" onClick={() => askRetire(d)}>
                    Vyřadit
                  </Table.Action>
                </>
              )}
            </Table.Actions>
          );
        },
      },
    ],
    [live, move, askRetire, reorder.isPending, readOnly, navigate, seasonId],
  );

  const code = season?.code ?? "";
  return (
    <>
      <AppTopbar crumbs={[{ label: "Sezóny", href: "/admin/sezony" }, { label: `Dokumenty ${code}` }]} />
      <Page>
        <Page.Header
          title={code ? `Dokumenty sezóny ${code}` : "Dokumenty sezóny"}
          subtitle="dokumenty, se kterými vedoucí souhlasí při registraci do sezóny"
          actions={
            !readOnly && (
              <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
                Přidat dokument
              </Button>
            )
          }
        />
        <Page.Content stack gap="lg">
          <Table<SeasonDocument>
            framed
            rowKey={(d) => d.id}
            columns={columns}
            rows={[...live, ...retired]}
            isLoading={isLoading}
            error={error}
            onRetry={() => void refetch()}
            empty={
              <EmptyState
                title="Žádné dokumenty"
                description="Sezóna zatím nemá žádné dokumenty ke souhlasu. Registrace do sezóny proto žádný souhlas nevyžaduje."
              />
            }
          />
        </Page.Content>
      </Page>

      {modal.kind === "create" && <CreateDocumentModal seasonId={seasonId} onClose={close} />}
      {modal.kind === "replace" && <ReplaceFileModal doc={modal.doc} onClose={close} />}
      {modal.kind === "rename" && <RenameModal doc={modal.doc} onClose={close} />}
    </>
  );
}

type SourceKind = "file" | "url";

const SOURCE_OPTIONS: { value: SourceKind; label: string }[] = [
  { value: "file", label: "Soubor PDF" },
  { value: "url", label: "Odkaz na stránku" },
];

/** The file-or-link half of both dialogs. The server checks the link too
 * (http/https only); this only keeps the button off until it looks like
 * one. */
function useSourceFields(initialKind: SourceKind = "file", initialUrl = "") {
  const [kind, setKind] = useState<SourceKind>(initialKind);
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState(initialUrl);
  const source: DocumentSource | null =
    kind === "file" ? (file ? { file } : null) : /^https?:\/\/\S+$/i.test(url.trim()) ? { url: url.trim() } : null;
  const fields = (
    <>
      <SegmentedControl
        label="Dokument je"
        options={SOURCE_OPTIONS}
        value={kind}
        onChange={setKind}
      />
      {kind === "file" ? (
        <FileUploadField
          label="Soubor (PDF)"
          accept={PDF_ACCEPT}
          maxSize={MAX_UPLOAD_BYTES}
          onSelect={setFile}
          preview={file ? <Text>{file.name}</Text> : undefined}
        />
      ) : (
        <Input
          label="Adresa stránky"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
          required
        />
      )}
    </>
  );
  return { source, fields };
}

function CreateDocumentModal({ seasonId, onClose }: { seasonId: ID; onClose: () => void }) {
  const [name, setName] = useState("");
  const { source, fields } = useSourceFields();
  const create = useCreateSeasonDocument(seasonId);
  return (
    <FormModal
      open
      title="Přidat dokument"
      submitLabel="Přidat"
      error={create.error}
      busy={create.isPending}
      canSubmit={!!name.trim() && !!source}
      onSubmit={() => {
        if (source) create.mutate({ name: name.trim(), source }, { onSuccess: onClose });
      }}
      onClose={onClose}
    >
      <Input
        label="Název"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="např. Zásady zpracování osobních údajů"
        required
      />
      {fields}
    </FormModal>
  );
}

function ReplaceFileModal({ doc, onClose }: { doc: SeasonDocument; onClose: () => void }) {
  const currentUrl = doc.current?.url ?? "";
  const { source, fields } = useSourceFields(currentUrl ? "url" : "file", currentUrl);
  const replace = useReplaceSeasonDocumentFile();
  const unchanged = !!source && "url" in source && source.url === currentUrl;
  return (
    <FormModal
      open
      title={`Nová verze: ${doc.name}`}
      submitLabel="Uložit novou verzi"
      error={replace.error}
      busy={replace.isPending}
      canSubmit={!!source && !unchanged}
      onSubmit={() => {
        if (source) replace.mutate({ documentId: doc.id, source }, { onSuccess: onClose });
      }}
      onClose={onClose}
    >
      <Alert variant="warn">
        Týmy, které souhlasily s předchozí verzí, budou při příští registraci požádány o souhlas
        znovu. Předchozí verze zůstane uložená.
      </Alert>
      {fields}
    </FormModal>
  );
}

function RenameModal({ doc, onClose }: { doc: SeasonDocument; onClose: () => void }) {
  const [name, setName] = useState(doc.name);
  const rename = useRenameSeasonDocument();
  return (
    <FormModal
      open
      title="Přejmenovat dokument"
      error={rename.error}
      busy={rename.isPending}
      canSubmit={!!name.trim() && name.trim() !== doc.name}
      onSubmit={() => rename.mutate({ documentId: doc.id, name: name.trim() }, { onSuccess: onClose })}
      onClose={onClose}
    >
      <Input label="Název" value={name} onChange={(e) => setName(e.target.value)} required />
    </FormModal>
  );
}
